import { isAudit as checkIsAudit } from "PFApp/activities/helpers";
import { useAvailableBookingCategories } from "PFApp/booking/components/booking_category_select/use_available_booking_categories";
import { TemplateKey } from "PFApp/constants/templates";
import { checkIsBookingCategoryLinked } from "PFApp/helpers/check_is_booking_category_linked";
import { useTemplate } from "PFApp/hooks";
import { isActivityFieldPermitted } from "PFCore/helpers/activity_fields_permissions";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { AccessLevel, Activity, Profile, Subtemplate, Template } from "PFCore/types";
import { useCallback } from "react";

type UseBookingCategoryLinkParams = {
  template: Template;
};

type GetBookingCategoryLinkHelpersParams = {
  parentActivity?: Activity;
  activityCreator: Profile | null;
  subtemplate: Subtemplate | null;
  bookingCategoryId: number | undefined;
};

export const useBookingCategoryLink = ({ template }: UseBookingCategoryLinkParams) => {
  const { data: currentProfile } = useCurrentProfile();
  const getAvailableBookingCategories = useAvailableBookingCategories();
  const engagementTemplate = useTemplate(
    checkIsAudit(template) ? TemplateKey.AuditEngagement : TemplateKey.Engagement
  );

  const isPropertyLinked =
    !!engagementTemplate?.schema?.properties.booking_category_id?.linked_from_engagement;

  const getBookingCategoryLinkHelpers = useCallback(
    ({
      parentActivity,
      activityCreator,
      subtemplate,
      bookingCategoryId
    }: GetBookingCategoryLinkHelpersParams) => {
      const isPermittedToWriteBookingCategoryField = isActivityFieldPermitted({
        currentProfile,
        activityCreator,
        fieldName: "booking_category",
        accessLevel: AccessLevel.ReadWrite
      });
      const isBookingCategorySelectDisabled = !template || !isPermittedToWriteBookingCategoryField;
      const availableBookingCategoryValues = getAvailableBookingCategories({
        activityData: [
          {
            activityTemplateId: template.id,
            activitySubtemplateKey: subtemplate?.subtemplate_key,
            parentActivitySubtemplateKey: parentActivity?.subtemplate_key
          }
        ]
      });

      const { isLinked, canLink } = !isBookingCategorySelectDisabled
        ? checkIsBookingCategoryLinked({
            activity: parentActivity,
            activityTemplate: engagementTemplate,
            bookingCategoryId,
            availableBookingCategoryValues
          })
        : { isLinked: false, canLink: false };

      const parentBookingCategoryId = parentActivity?.booking_category_id;

      return {
        isLinked,
        canLink,
        parentBookingCategoryId
      };
    },
    [currentProfile, engagementTemplate, getAvailableBookingCategories, template]
  );

  return {
    isPropertyLinked,
    getBookingCategoryLinkHelpers
  };
};
